import React, { useEffect, useRef } from 'react'
import { Text, View, Tag, Button } from '@/components'
import { Theme, variantProvider } from '@/app'
import type { UseProfilePrompt } from '../../useProfilePrompt'
import { FeedDimensions } from '../shared'
import { globalState } from '@codeleap/store'

export type BaseCardProps = React.PropsWithChildren<{
  prompt: UseProfilePrompt
}>

const portalState = globalState({})

export const BaseCard = (props: BaseCardProps) => {
  const { children, prompt } = props

  const profilePrompt = prompt?.item?.profile_prompt
  const hasImage = profilePrompt?.image

  const hasOpenPortal = portalState.use(s => s?.[prompt?.item?.profile_prompt?.key] ?? false)

  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (hasOpenPortal) {
      elementRef.current?.scrollTo?.({
        top: elementRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [hasOpenPortal])

  return (
    <View ref={elementRef} style={styles.wrapper}>
      <Tag
        variants={['backgroundColor:primary1']}
        textProps={{ variants: ['p3', 'color:neutral9'] }}
        leftIcon='user'
        debugName=''
        text='Complete your profile'
        leftIconProps={{
          // @ts-ignore
          variants: ['iconSize:1'],
          color: Theme.colors.light.neutral5
        }}
      />

      <View variants={['gap:2', 'fullWidth', 'column']} style={{ pointerEvents: prompt.isAnswered ? 'none' : 'auto' }}>
        {!hasImage ? null : (
          <img src={profilePrompt.image} style={styles.image} />
        )}

        <View variants={['gap:0.5', 'fullWidth', 'column']}>
          <Text variants={['h2']} text={profilePrompt.title} />
          <Text variants={['p1', 'color:neutral5']} text={profilePrompt.description} />
        </View>

        {children}
      </View>

      {hasOpenPortal ? <div style={{ minHeight: 200 }} /> : null}

      <Button
        debugName='answer prompt profile'
        text={prompt.isAnswered ? 'Answer submitted!' : 'Submit'}
        onPress={prompt.onUpdateProfile}
        disabled={!prompt.canAnswer ? true : prompt.isAnswered}
        variants={['marginTop:auto', 'fullWidth', 'large']}
        style={{ minHeight: Theme.values.itemHeight.default }}
      />
    </View>
  )
}

BaseCard.setOpenPortal = (prompt: BaseCardProps['prompt'], open: boolean) => {
  portalState.set({ [prompt?.item?.profile_prompt?.key]: open })
}

const styles = variantProvider.createComponentStyle(theme => ({
  wrapper: {
    height: FeedDimensions.HEIGHT,
    width: FeedDimensions.WIDTH,
    backgroundColor: theme.colors.neutral1,
    overflowX: 'hidden',
    overflowY: 'auto',
    display: 'flex',
    ...theme.presets.column,
    ...theme.presets.justifyStart,
    ...theme.presets.alignStart,
    ...theme.spacing.padding(2),
    ...theme.spacing.gap(3),

    [theme.media.down(FeedDimensions.mobileBreakpoint)]: {
      width: FeedDimensions.WIDTH_WITHOUT_SPACING,
      height: FeedDimensions.HEIGHT_WITHOUT_SPACING,
    }
  },
  image: {
    ...theme.presets.fullWidth,
    objectFit: 'contain',
    aspectRatio: 16 / 9,
  },
}), true)
